import { plural } from '../../utils/plural';
import { actionCounts } from '../../utils/actionCounts';
import WorkflowCard, { WorkflowBreadcrumb } from '../../components/WorkflowCard';
import ActionDetailModal from '../../components/Forms/ActionDetailModal';
import { priorityLabel } from '../../utils/presentation';
import { isTaskOverdue } from '../../utils/taskPresentation';
import { formatTaskHistory, statusLabel } from '../../utils/taskPresentation';
import RiskBadge from '../../components/RiskBadge';
import usePageTitle from '../../hooks/usePageTitle';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckSquare,
  Filter,
  Calendar as CalendarIcon,
  CheckCircle,
  Clock,
  AlertTriangle,
  User,
  Search,
  MessageSquare,
  FileText,
  Edit3,
  Play,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Task, TaskStatus, User as ApiUser } from '../../types';
import { tasksService, usersService, clientsService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePortalScope } from '../../hooks/usePortalScope';
import { formatShortDate } from '../../utils/dateUtils';
import './ClientPortalActions.css';

type StatusFilter = 'all' | TaskStatus | 'overdue';

const ClientPortalActions: React.FC = () => {
  usePageTitle('Actions');
  const [searchParams] = useSearchParams();
  const { currentUser, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const { clientId, managerId, scopeFilter } = usePortalScope();
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [clientName, setClientName] = useState('your organisation');
  const headingClientId = clientId || currentUser?.client_id;
  useEffect(() => { if (headingClientId) clientsService.getClientById(headingClientId).then(client => setClientName(client.name)).catch(() => {}); }, [headingClientId]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [blockingTask, setBlockingTask] = useState<string | null>(null);
  const [blockingComment, setBlockingComment] = useState('');
  const [commentDrafts, setCommentDrafts] = useState<Record<string, string>>({});
  const [showAllHistory, setShowAllHistory] = useState<Record<string, boolean>>({});
  const taskRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const linkedTaskId = searchParams.get('task');

  const loadData = useCallback(async () => {
    if (!scopeFilter) {
      setTasks([]);
      setUsers([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      void usersService.getUsers().then(setUsers).catch(() => {});
      const tasksData = await tasksService.getTasks(scopeFilter);

      setTasks(tasksData);
    } catch (err) {
      console.error('Error loading actions:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, managerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!authLoading) {
      loadData();
    }
  }, [authLoading, loadData]);

  useEffect(() => {
    if (!linkedTaskId || !tasks.some((task) => task.id === linkedTaskId)) return;
    if (tasks.find(task => task.id === linkedTaskId)?.workflow) setDetailTaskId(linkedTaskId);
    setExpandedTask(linkedTaskId);
    taskRefs.current[linkedTaskId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [linkedTaskId, tasks]);

  const getStatusInfo = (task: Task) => {
    const isOverdue =
      isTaskOverdue(task);

    if (isOverdue) {
      return { class: 'status-overdue', label: 'Overdue', icon: AlertTriangle };
    }

    if (task.due_by && new Date(task.due_by).toDateString() === new Date().toDateString() && !['complete', 'cancelled'].includes(task.status)) return { class: 'status-due-today', label: 'Due today', icon: Clock };
    switch (task.status) {
      case 'not_started':
        return { class: 'status-not-started', label: 'Not Started', icon: Clock };
      case 'acknowledged':
        return { class: 'status-acknowledged', label: 'Acknowledged', icon: CheckSquare };
      case 'started':
        return { class: 'status-started', label: 'In Progress', icon: Play };
      case 'blocked':
        return { class: 'status-blocked', label: 'Blocked', icon: AlertTriangle };
      case 'complete':
        return { class: 'status-complete', label: 'Complete', icon: CheckCircle };
      default:
        return { class: 'status-default', label: task.status, icon: Clock };
    }
  };

  const filterTasks = () => {
    return tasks.filter((task) => {
      if (mineOnly && task.responsible_user_id !== currentUser?.id) return false;
      // Search filter
      if (searchTerm && !task.description.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'overdue') {
          const isOverdue =
            isTaskOverdue(task);
          if (!isOverdue) return false;
        } else if (task.status !== statusFilter) {
          return false;
        }
      }

      return true;
    });
  };

  const handleChangeStatus = async (taskId: string, newStatus: TaskStatus, comment?: string) => {
    try {
      await tasksService.changeStatus(taskId, {
        new_status: newStatus,
        ...(comment ? { comment } : {}),
      });
      await loadData();
      showToast({type: 'success', title: `Action ${statusLabel(newStatus).toLowerCase()}`});
    } catch (err) {
      console.error('Error changing task status:', err);
      showToast({
        type: 'error',
        title: 'Could not update action',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const handleBlockTask = async (taskId: string) => {
    const comment = blockingComment.trim();
    if (!comment) return;
    await handleChangeStatus(taskId, 'blocked', comment);
    setBlockingTask(null);
    setBlockingComment('');
  };

  const handleAddComment = async (taskId: string) => {
    const comment = (commentDrafts[taskId] ?? '').trim();
    if (!comment) return;
    try {
      await tasksService.addComment(taskId, {
        user_id: currentUser?.id ?? '',
        user_name: currentUser?.name ?? '',
        comment,
      });
      setCommentDrafts((prev) => ({ ...prev, [taskId]: '' }));
      showToast({type: 'success', title: 'Comment added'});
      await loadData();
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Could not add comment',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const getHistoryIcon = (actionType?: string) => {
    if (actionType === 'commented') return MessageSquare;
    if (actionType === 'status_change' || actionType === 'status_changed') return Edit3;
    return FileText;
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await tasksService.completeTask(taskId);
      showToast({type: 'success', title: 'Action completed'});
      await loadData();
    } catch (err) {
      console.error('Error completing task:', err);
      showToast({
        type: 'error',
        title: 'Could not complete action',
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    }
  };

  const toggleTaskExpand = (taskId: string) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
  };

  const filteredTasks = filterTasks().sort((a, b) => {
    const rank = (task: Task) => ['complete', 'cancelled'].includes(task.status) ? -1 : (task.priority === 'critical' ? 8 : task.priority === 'high' ? 4 : 0) + (task.due_by && new Date(task.due_by).toDateString() !== new Date().toDateString() && new Date(task.due_by) < new Date() ? 2 : 0);
    return rank(b) - rank(a);
  });

  const counts = actionCounts(tasks);
  // Stats
  const topLevelTasks = tasks.filter(task => !task.parent_task_id);
  const stats = {
    total: topLevelTasks.length,
    notStarted: topLevelTasks.filter((t) => t.status === 'not_started').length,
    acknowledged: topLevelTasks.filter((t) => t.status === 'acknowledged').length,
    started: topLevelTasks.filter((t) => t.status === 'started').length,
    blocked: topLevelTasks.filter((t) => t.status === 'blocked').length,
    complete: counts.complete,
    overdue: counts.overdue,
  };

  if (loading) {
    return (
      <div className="client-actions-loading">
        <div className="loading-spinner" />
        <p>Loading your actions...</p>
      </div>
    );
  }

  if (!clientId && !managerId) {
    return (
      <div className="client-actions-loading">
        <p>No account scope found. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div data-tour="portal-actions" className="client-portal-actions">
      <div className="page-header">
        <div className="header-content">
          <ActionDetailModal isOpen={!!detailTaskId} taskId={detailTaskId} onClose={() => setDetailTaskId(null)} onUpdated={loadData} /><h1>
            <CheckSquare size={28} />
            Actions for {clientName}
          </h1>
          <p>View and manage actions for your organisation</p><button type="button" className="mine-toggle" aria-pressed={mineOnly} onClick={() => setMineOnly(!mineOnly)}>Mine only</button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="actions-stats">
        <button
          className={`stat-pill ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <span className="stat-count">{stats.total}</span>
          <span className="stat-label">All</span>
        </button>
        <button
          className={`stat-pill stat-not-started ${statusFilter === 'not_started' ? 'active' : ''}`}
          onClick={() => setStatusFilter('not_started')}
        >
          <span className="stat-count">{stats.notStarted}</span>
          <span className="stat-label">Not Started</span>
        </button>
        <button
          className={`stat-pill stat-acknowledged ${statusFilter === 'acknowledged' ? 'active' : ''}`}
          onClick={() => setStatusFilter('acknowledged')}
        >
          <span className="stat-count">{stats.acknowledged}</span>
          <span className="stat-label">Acknowledged</span>
        </button>
        <button
          className={`stat-pill stat-started ${statusFilter === 'started' ? 'active' : ''}`}
          onClick={() => setStatusFilter('started')}
        >
          <span className="stat-count">{stats.started}</span>
          <span className="stat-label">In Progress</span>
        </button>
        <button
          className={`stat-pill stat-blocked ${statusFilter === 'blocked' ? 'active' : ''}`}
          onClick={() => setStatusFilter('blocked')}
        >
          <span className="stat-count">{stats.blocked}</span>
          <span className="stat-label">Blocked</span>
        </button>
        <button
          className={`stat-pill stat-complete ${statusFilter === 'complete' ? 'active' : ''}`}
          onClick={() => setStatusFilter('complete')}
        >
          <span className="stat-count">{stats.complete}</span>
          <span className="stat-label">Complete</span>
        </button>
        {stats.overdue > 0 && (
          <button
            className={`stat-pill stat-overdue ${statusFilter === 'overdue' ? 'active' : ''}`}
            onClick={() => setStatusFilter('overdue')}
          >
            <span className="stat-count">{stats.overdue}</span>
            <span className="stat-label">Overdue</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="actions-toolbar">
        <div className="search-container">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search actions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-info">
          <Filter size={16} />
          Showing {filteredTasks.length} of {plural(tasks.length, 'action')}
        </div>
      </div>

      {/* Actions List */}
      <div className="actions-list">
        {filteredTasks.length === 0 ? (
          <div className="empty-state">
            <CheckCircle size={48} />
            <h3>No Actions Found</h3>
            <p>
              {tasks.length === 0
                ? 'You have no actions assigned yet.'
                : 'No actions match your current filters.'}
            </p>
          </div>
        ) : (
          filteredTasks.map((task) => {
            if (task.workflow) return <WorkflowCard key={task.id} task={task} currentUserId={currentUser?.id} onOpen={setDetailTaskId} onComplete={async id => { await tasksService.completeTask(id); await loadData(); }} onStatusChange={async (id, status) => { await tasksService.changeStatus(id, {new_status: status}); await loadData(); }} />;
            const statusInfo = getStatusInfo(task);
            const StatusIcon = statusInfo.icon;
            const isExpanded = expandedTask === task.id;
            const assignedUser = users.find((u) => u.id === task.responsible_user_id);
            const history = [...(task.action_history ?? [])].sort(
              (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
            const visibleHistory = showAllHistory[task.id] ? history : history.slice(0, 3);

            return (
              <div
                key={task.id}
                ref={(element) => { taskRefs.current[task.id] = element; }}
                className={`action-card ${statusInfo.class}`}
              >
                <div className="action-main" onClick={() => toggleTaskExpand(task.id)}>
                  <div className="action-status-icon">
                    <StatusIcon size={20} />
                  </div>

                  <div className="action-content">
                    <div className="action-description">
                      <WorkflowBreadcrumb task={task} onOpen={setDetailTaskId} />{task.description}
                      {task.source_lab_report_id && <a className="rectification-link" onClick={e => e.stopPropagation()} href={`/portal/lab-reports/${task.source_lab_report_id}`}>View lab report and incident</a>}
                      {task.risk_likelihood != null && task.risk_consequence != null && <RiskBadge likelihood={task.risk_likelihood} consequence={task.risk_consequence} />}
                      {task.category === 'rectification' && (
                        <span
                          className="lab-incident-chip"
                          title="Raised from a positive lab result"
                        >
                          Lab incident
                        </span>
                      )}
                    </div>
                    <div className="action-meta">
                      {task.priority && <span className={`portal-priority priority-${task.priority}`}>{priorityLabel(task.priority)}</span>}
                      {task.due_by && (
                        <span className="meta-item">
                          <CalendarIcon size={14} />
                          Due: {formatShortDate(task.due_by)}
                        </span>
                      )}
                      {assignedUser && (
                        <span className="meta-item">
                          <User size={14} />
                          {assignedUser.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="action-right">
                    <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>
                    <button className="expand-btn" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="action-details">
                    {/* Subtasks */}
                    {task.subtasks && task.subtasks.length > 0 && (
                      <div className="detail-section">
                        <h4>Subtasks</h4>
                        <ul className="subtasks-list">
                          {task.subtasks.map((subtask, idx) => (
                            <li key={idx}>
                              <CheckSquare size={14} />
                              {subtask}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Action History */}
                    {task.action_history && task.action_history.length > 0 && (
                      <div className="detail-section">
                        <h4>History</h4>
                        <ul className="history-list" aria-label="Action history">
                          {visibleHistory.map((entry, idx) => {
                            const HistoryIcon = getHistoryIcon(entry.action_type);
                            return (
                            <li key={`${entry.timestamp}-${idx}`}>
                              <HistoryIcon size={14} />
                              <span className="history-text">
                                <span className="history-user">{entry.user_name ?? 'User'}</span>
                                {formatTaskHistory(entry.details ?? entry.description)}
                              </span>
                              <span className="history-date">
                                {formatShortDate(entry.timestamp)}
                              </span>
                            </li>
                          );})}
                        </ul>
                        {history.length > 3 && (
                          <button
                            className="history-toggle"
                            onClick={() => setShowAllHistory((prev) => ({ ...prev, [task.id]: !prev[task.id] }))}
                          >
                            {showAllHistory[task.id] ? 'Collapse' : 'Show all'}
                          </button>
                        )}
                      </div>
                    )}

                    <div className="comment-form">
                      <textarea
                        placeholder="Add a comment..."
                        value={commentDrafts[task.id] ?? ''}
                        onChange={(e) => setCommentDrafts((prev) => ({ ...prev, [task.id]: e.target.value }))}
                        rows={3}
                      />
                      <button
                        className="btn btn-secondary"
                        disabled={!(commentDrafts[task.id] ?? '').trim()}
                        onClick={() => handleAddComment(task.id)}
                      >
                        <MessageSquare size={16} />
                        Add comment
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="action-buttons">
                      {task.allowed_transitions?.includes('acknowledged') && (
                        <button className="btn btn-secondary" onClick={() => handleChangeStatus(task.id, 'acknowledged')}>
                          <CheckSquare size={16} /> Acknowledge
                        </button>
                      )}
                      {task.allowed_transitions?.includes('started') && task.status !== 'blocked' && (
                        <button
                          className="btn btn-secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleChangeStatus(task.id, 'started');
                          }}
                        >
                          <Play size={16} />
                          Start Working
                        </button>
                      )}
                      {task.allowed_transitions?.includes('blocked') && (
                        <button className="btn btn-danger" onClick={() => { setBlockingTask(task.id); setBlockingComment(''); }}>
                          <AlertTriangle size={16} /> Blocked…
                        </button>
                      )}
                      {task.allowed_transitions?.includes('started') && task.status === 'blocked' && (
                        <button className="btn btn-secondary" onClick={() => handleChangeStatus(task.id, 'started')}>
                          <Play size={16} /> Resume Working
                        </button>
                      )}
                      {task.allowed_transitions?.includes('complete') && (
                        <button
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCompleteTask(task.id);
                          }}
                        >
                          <CheckCircle size={16} />
                          Mark Complete
                        </button>
                      )}
                    </div>
                    {blockingTask === task.id && (
                      <div className="blocked-form">
                        <textarea
                          placeholder="Why is this blocked?"
                          value={blockingComment}
                          onChange={(e) => setBlockingComment(e.target.value)}
                          rows={3}
                        />
                        <div className="form-actions">
                          <button className="btn btn-secondary" onClick={() => { setBlockingTask(null); setBlockingComment(''); }}>Cancel</button>
                          <button className="btn btn-danger" disabled={!blockingComment.trim()} onClick={() => handleBlockTask(task.id)}>Confirm</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ClientPortalActions;
